//hooks
import { useState, useEffect } from "react";
import useGlobal from "store/store";
import { useTranslation } from "react-i18next";
import useApi from "hooks/useApi";
// Mui
import { Box, Typography, Select, MenuItem } from "@mui/material";
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
//api
import { getTribePupilsSeniors, getPupilsRoles } from 'api/api';
//components
import { CustomBusy } from "Components/Common/CustomBusy";
import { MyInputStyle } from "Components/TribeComponents/OpenSignReqs";
import PupilsRolesTable from "Components/TribeComponents/PupilsRolesTable";
//assets
import inactiveChildren from '../assets/inactiveChildren.svg'
import { permanentInfo } from "data/permanentInfo";

const TribeRolesPage = ({ tribe }) => {
    const { t } = useTranslation();
    const getPupilsByTribe = useApi(getTribePupilsSeniors);
    const getRoles = useApi(getPupilsRoles);

    const [pupilsList, setPupilsList] = useState([]);
    const [pupilRolesList, setPupilRolesList] = useState([]);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [pupilsUpdateRoles, setPupilsUpdateRoles] = useState(0);
    const [ready, setReady] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [classNo, setClassNo] = useState('allPupils');
    const [displayMode, setDisplayMode] = useState('all');

    const [user, setUser] = useGlobal((state) => state.user, (actions) => actions.setUser);


    useEffect(() => {
        if (tribe?.UnitCode) {
            setSearchValue("");
            setClassNo("allPupils");
            setDisplayMode("all");
            setReady(false);
            setPupilsList([]);
            fetchPupils();
        }
    }, [tribe, pupilsUpdateRoles]);

    useEffect(() => {
        let timer;
        if (getPupilsByTribe.loading) {
            setLoadingProgress(0);
            timer = setInterval(() => {
                setLoadingProgress(prev => Math.min(prev + 1, 99));
            }, 980);
        } else {
            setLoadingProgress(100);
        }

        return () => clearInterval(timer);
    }, [getPupilsByTribe.loading]);


    const fetchPupils = async () => {
        try {
            // Single call to new backend service
            const allPupils = await getPupilsByTribe.requestPromise({ tribeCode: tribe.UnitCode });
            const pupilRolesApi = await getRoles.requestPromise();
            setPupilRolesList(pupilRolesApi.d.results);
            setPupilsList(allPupils); // assuming allPupils is an array
            setReady(true);
        } catch (err) {
            console.error("Failed to load pupils:", err);
            setPupilsList([]);
            setReady(false);
        }
    };

    const getFilteredRows = () => {
        let filtered = pupilsList;

        // Filter by class (only if not "allPupils")
        if (classNo && classNo !== "allPupils") {
            filtered = filtered.filter(pupil => pupil.Class === classNo);
        }

        // Filter by search value (name)
        if (searchValue.trim()) {
            const search = searchValue.trim().toLowerCase();
            filtered = filtered.filter(
                pupil =>
                    (pupil.Pupil_FirstName && pupil.Pupil_FirstName.toLowerCase().includes(search)) ||
                    (pupil.Pupil_LastName && pupil.Pupil_LastName.toLowerCase().includes(search))
            );
        }

        // Filter by display mode
        if (displayMode === "noRole") {
            filtered = filtered.filter(
                pupil =>
                    !pupil.Role || (typeof pupil.Role === "object" && !pupil.Role.RoleCode)
            );
        } else if (displayMode === "noPhone") {
            filtered = filtered.filter(
                pupil => !pupil.Phone1 || pupil.Phone1.trim() === ""
            );
        }

        // Sort: pupils with role first, then without role
        filtered = filtered.slice().sort((a, b) => {
            const aHasRole = !!(a.Role && (typeof a.Role === "string" ? a.Role : a.Role.RoleCode));
            const bHasRole = !!(b.Role && (typeof b.Role === "string" ? b.Role : b.Role.RoleCode));
            return (aHasRole === bHasRole) ? 0 : aHasRole ? 1 : -1;
        });

        return filtered;
    };

    return (
        <>
            {/* Desktop View */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" marginLeft="2rem">
                        <EventAvailableIcon sx={{ fontSize: { xs: '1rem', md: '1.5rem' } }} />
                        <Typography ml={1} variant="mediumDarkGreyBoldRes">
                            {`${t("pupilTribe")} ${tribe?.UnitName} ${t("seniors")} (${pupilsList.length})`}
                        </Typography>
                    </Box>

                </Box>
                <SortLine
                    setSearchValue={setSearchValue}
                    classNo={classNo}
                    setClassNo={setClassNo}
                    permanentInfo={permanentInfo}
                    displayMode={displayMode}
                    setDisplayMode={setDisplayMode}
                />
                {getPupilsByTribe.loading || !ready ? (
                    <CustomBusy text={`${t("willTakeFewMoments")} ${loadingProgress}%`} />
                ) : (
                    <PupilsRolesTable
                        rows={getFilteredRows()}
                        pupilsUpdateRoles={pupilsUpdateRoles}
                        setPupilsUpdateRoles={setPupilsUpdateRoles}
                        pupilRolesList={pupilRolesList}
                        tribe={tribe}
                        user={user}
                    />
                )}
            </Box >

            {/* Mobile View */}
            <Box Box sx={{ display: { xs: "flex", md: "none" } }
            } flexDirection="column" alignItems="center" >
                <img alt="..." src={inactiveChildren} width="150px" height="150px" />
                <Typography sx={{ color: "#ff0000", fontSize: "1rem", fontWeight: "bold" }}>
                    {t("tableNotActive")}
                </Typography>
            </Box >
        </>
    );
}

const SortLine = ({
    setSearchValue,
    classNo,
    setClassNo,
    displayMode,
    setDisplayMode
}) => {
    const { t } = useTranslation();
    const classesArray = [{ className: 'י', classCode: '8' }, { className: 'יא', classCode: '9' }, { className: 'יב', classCode: '10' }];
    return (
        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" margin="1rem">
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" marginRight="2rem">
                {/* Search Input */}
                <MyInputStyle
                    variant="outlined"
                    placeholder={t('searchPupil')}
                    onChange={e => setSearchValue(e.target.value)}
                    InputProps={{
                        endAdornment: (<SearchTwoToneIcon sx={{ color: '#3a7735' }} />)
                    }}
                />
                {/* Class Select */}
                <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" marginRight="2rem" marginLeft="2rem">
                    <Typography>{t("chooseClass")}</Typography>
                    <Select
                        sx={{ paddingLeft: "1rem", minWidth: 80 }}
                        variant='standard'
                        value={classNo}
                        onChange={e => setClassNo(e.target.value)}
                    >
                        <MenuItem value="allPupils">{t("allClasses")}</MenuItem> {/* Add this line */}
                        {classesArray.map((childClass, i) => (
                            <MenuItem value={childClass.classCode} key={i}>{childClass.className}</MenuItem>
                        ))}
                    </Select>
                </Box>
                {/* Display Mode Select */}
                <Box display="flex" flexDirection="row" alignItems="center" marginLeft="2rem">
                    <Typography>{t("displayMode")}</Typography>
                    <Select
                        sx={{ paddingLeft: "1rem", minWidth: 120 }}
                        variant='standard'
                        value={displayMode}
                        onChange={e => setDisplayMode(e.target.value)}
                    >
                        <MenuItem value="all">{t("displayAllPupils")}</MenuItem>
                        <MenuItem value="noRole">{t("displayNoRolePupils")}</MenuItem>
                        <MenuItem value="noPhone">{t("displayNoPhonePupils")}</MenuItem>
                    </Select>
                </Box>
            </Box>
        </Box>
    );
};
export default TribeRolesPage;